import React from "react";
import Button from "react-bootstrap/Button";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Alert from "react-bootstrap/Alert";
import SaveDispositionPopover from "components/SaveDispositionPopover";
import PushToPharmacyPopover from "components/PushToPharmacyPopover";
import CreateActivity from "components/SwitchDetails/CreateActivity";
import ChangeSwitchTemplate from "components/SwitchDetails/ChangeSwitchTemplate";
import UnlinkDeclinedFax from "components/UnlinkDeclinedFax/UnlinkDeclinedFax";
import RawSubmitForReview from "components/FaxDisposition/RawSubmitForReview";
import DiscardRaw from "components/FaxDisposition/DiscardRaw";
import { isLoggedInUserAWAdmin } from "services/AWUtilityService";
import { hasRoleIn } from "rbac/RbacHelper";
import PERMISSIONS from "rbac/Permissions.json";
import { WorkflowConstants } from "lookup/WorkflowConstants";
import { useSafeNavigate } from "utils/useSafeNavigate";
import { isFaxDispositionActionVisible } from "views/Raw/FaxDisposition/faxDispositionActionVisibility";
import "./FaxDispositionActions.css";

const FaxDispositionActions = ({
  actionsLayout,
  rawDetails,
  rawId,
  unlinkConfig,
  formValues,
  contextIsRaw,
  changeDisposition = false,
  disableActions,
  submittingDisposition,
  selectableDispositions,
  onSubmitDisposition,
  onSubmitPushToPharmacy,
  onSubmitForReview,
  onUnclaimRaw,
  onDiscardRaw,
  elementIdPrefix = "switchdisposition",
}) => {
  const navigate = useSafeNavigate();
  const {
    rows = [],
    buttonMinHeight = "38px",
    showOwnerAlert = false,
    actionVisibility = {},
  } = actionsLayout ?? {};

  const actionVisibilityContext = {
    formValues,
    rawDetails,
    selectableDispositions,
    contextIsRaw,
    changeDisposition,
    disableActions,
    submittingDisposition,
  };

  const renderAction = (actionKey) => {
    if (
      !isFaxDispositionActionVisible(
        actionKey,
        actionVisibility,
        actionVisibilityContext
      )
    ) {
      return null;
    }

    switch (actionKey) {
      case "unclaim":
        return (
          <Button
            id={`${elementIdPrefix}-btn-unclaim`}
            variant="info"
            onClick={onUnclaimRaw}
            disabled={disableActions}
            className="btn-block"
          >
            Unclaim
          </Button>
        );
      case "discard":
        return (
          <DiscardRaw
            onDiscard={onDiscardRaw}
            disabled={disableActions}
            elementIdPrefix={elementIdPrefix}
          />
        );
      case "saveDisposition":
        return (
          <SaveDispositionPopover
            disallowEditing={submittingDisposition}
            disableActions={disableActions}
            values={formValues}
            submitDisposition={onSubmitDisposition}
          />
        );
      case "createActivity":
        return (
          <CreateActivity
            wfItemId={rawDetails.wfItemId}
            isMemberActivityCreatable={rawDetails.isMemberActivityCreatable}
            contextIsRaw={contextIsRaw}
            disableButton={
              !(
                isLoggedInUserAWAdmin() ||
                hasRoleIn([PERMISSIONS.PERM_AW_CREATE_ACTIVITY])
              )
            }
            selectedPrescriberUuids={
              rawDetails.prescriberUuid ? [rawDetails.prescriberUuid] : []
            }
            selectedPrescriberNames={
              rawDetails.prescriberName ? [rawDetails.prescriberName] : []
            }
          />
        );
      case "submitForReview":
        return (
          <RawSubmitForReview
            onSubmitForReview={onSubmitForReview}
            disallowEditing={disableActions}
            contextIsRaw={contextIsRaw}
            elementIdPrefix={elementIdPrefix}
          />
        );
      case "changeTemplate":
        return (
          <ChangeSwitchTemplate
            rirSwitchId={rawDetails.switchId}
            selectedTemplate={rawDetails.templateIndicator}
            disableButton={!isLoggedInUserAWAdmin()}
            contextIsRaw={contextIsRaw}
            showDrugDisruptionOptions={rawDetails?.isDrugDisruption || false}
            showBioSimilarOptions={rawDetails?.isPriorityAgent || false}
          />
        );
      case "unlinkDeclinedFax":
        return (
          <UnlinkDeclinedFax
            rawId={rawId}
            rawDetails={rawDetails}
            unlinkConfig={unlinkConfig}
            history={navigate}
          />
        );
      case "pushToPharmacy":
        return (
          <PushToPharmacyPopover
            disallowEditing={submittingDisposition}
            disableActions={
              rawDetails.wfItemStatus !== WorkflowConstants.SUBMITTED_VIA_EHR
            }
            values={formValues}
            globalSwitchId={rawDetails.globalSwitchId}
            submitPushToPharmacy={onSubmitPushToPharmacy}
          />
        );
      default:
        return null;
    }
  };

  const renderActionSlot = (actionKey, slotIndex) => {
    const action = renderAction(actionKey);
    if (!action) {
      return null;
    }

    return (
      <div
        key={`${actionKey}-${slotIndex}`}
        className="fax-disposition-actions__slot"
      >
        {action}
      </div>
    );
  };

  const renderRow = (row, rowIndex) => {
    const visibleColumns = row.columns
      .map((column) => ({
        ...column,
        visibleActions: column.actions
          .map((actionKey) => ({
            actionKey,
            node: renderAction(actionKey),
          }))
          .filter(({ node }) => node),
      }))
      .filter((column) => column.visibleActions.length > 0);

    if (visibleColumns.length === 0) {
      return null;
    }

    return (
      <Row
        key={`action-row-${rowIndex}`}
        className="fax-disposition-actions__row align-items-start"
      >
        {visibleColumns.map((column, columnIndex) => (
          <Col
            key={`action-row-${rowIndex}-col-${columnIndex}`}
            md={column.width}
            className="fax-disposition-actions__col"
          >
            {column.visibleActions.map(({ actionKey }, actionIndex) =>
              renderActionSlot(actionKey, actionIndex)
            )}
          </Col>
        ))}
      </Row>
    );
  };

  return (
    <div
      className="fax-disposition-actions top-spacer"
      style={{ "--fax-disposition-btn-height": buttonMinHeight }}
    >
      {showOwnerAlert && !contextIsRaw && rawDetails.ownerId ? (
        <Alert variant={disableActions ? "warning" : "success"}>
          Claimed by: <strong>{rawDetails.ownerName}</strong>
        </Alert>
      ) : null}

      {rows.map((row, rowIndex) => renderRow(row, rowIndex))}
    </div>
  );
};

export default FaxDispositionActions;
